import React from 'react';
import { PiggyBank, Plus, Trash2, Clock, Target, AlertTriangle } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

/**
 * Секция управления сбережениями и финансовыми целями
 */
const SavingsSection = ({ 
  formData,
  handleInputChange,
  updateGoal,
  removeGoal,
  addGoal,
  errors
}) => {
  // Функция для обработки изменений в чрезвычайном фонде
  const handleEmergencyFundChange = (field, value) => {
    handleInputChange({ 
      target: { 
        name: `savings.emergencyFund.${field}`, 
        value,
        type: field === 'enabled' ? 'checkbox' : 'text',
        checked: value
      } 
    });
  };

  // Функция для обработки изменений в пенсионном фонде
  const handleRetirementFundChange = (field, value) => {
    handleInputChange({ 
      target: { 
        name: `savings.retirementFund.${field}`, 
        value,
        type: field === 'enabled' ? 'checkbox' : 'text',
        checked: value
      } 
    });
  };

  // Функция получения класса для приоритета цели
  const getPriorityClass = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300';
      case 'medium': return 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300';
      case 'low': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  return (
    <div className="mb-6">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <PiggyBank className="w-5 h-5 mr-2 text-blue-500" />
        Сбережения и финансовые цели
      </h3>

      {/* Секция чрезвычайного фонда (НЗ) */}
      <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800">
        <div className="flex items-start mb-3">
          <div className="flex-shrink-0 pt-0.5">
            <input
              type="checkbox"
              id="emergencyFundEnabled"
              checked={formData.savings.emergencyFund.enabled}
              onChange={(e) => handleEmergencyFundChange('enabled', e.target.checked)}
              className="form-checkbox"
            />
          </div>
          <div className="ml-3">
            <label htmlFor="emergencyFundEnabled" className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Чрезвычайный фонд (НЗ)
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Рекомендуется создать запас на 3-6 месяцев обязательных расходов
            </p>
          </div>
        </div>

        {formData.savings.emergencyFund.enabled && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2 ml-7">
            <div>
              <label htmlFor="emergencyFundTarget" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Целевая сумма (₽)
              </label>
              <div className="relative">
                <input
                  type="text"
                  id="emergencyFundTarget"
                  value={formData.savings.emergencyFund.target}
                  onChange={(e) => handleEmergencyFundChange('target', e.target.value)}
                  className={`form-input w-full pr-6 ${errors.emergencyFundTarget ? 'input-error' : ''}`}
                />
                <span className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400">₽</span>
              </div>
              {errors.emergencyFundTarget && (
                <p className="mt-1 text-xs text-red-500">{errors.emergencyFundTarget}</p>
              )}
            </div>
            <div>
              <label htmlFor="emergencyFundCurrent" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Уже накоплено (₽)
              </label>
              <div className="relative">
                <input
                  type="text"
                  id="emergencyFundCurrent"
                  value={formData.savings.emergencyFund.current}
                  onChange={(e) => handleEmergencyFundChange('current', e.target.value)}
                  className={`form-input w-full pr-6 ${errors.emergencyFundCurrent ? 'input-error' : ''}`}
                />
                <span className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400">₽</span>
              </div>
              {errors.emergencyFundCurrent && (
                <p className="mt-1 text-xs text-red-500">{errors.emergencyFundCurrent}</p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Секция пенсионных накоплений */}
      <div className="mb-6 p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-100 dark:border-purple-800">
        <div className="flex items-start mb-3">
          <div className="flex-shrink-0 pt-0.5">
            <input
              type="checkbox"
              id="retirementFundEnabled"
              checked={formData.savings.retirementFund.enabled}
              onChange={(e) => handleRetirementFundChange('enabled', e.target.checked)}
              className="form-checkbox"
            />
          </div>
          <div className="ml-3">
            <label htmlFor="retirementFundEnabled" className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Пенсионные накопления
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Долгосрочные накопления для обеспечения будущей пенсии
            </p>
          </div>
        </div>

        {formData.savings.retirementFund.enabled && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2 ml-7">
            <div>
              <label htmlFor="retirementFundTarget" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Целевая сумма (₽)
              </label>
              <div className="relative">
                <input
                  type="text"
                  id="retirementFundTarget"
                  value={formData.savings.retirementFund.target}
                  onChange={(e) => handleRetirementFundChange('target', e.target.value)}
                  className={`form-input w-full pr-6 ${errors.retirementFundTarget ? 'input-error' : ''}`}
                />
                <span className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400">₽</span>
              </div>
              {errors.retirementFundTarget && (
                <p className="mt-1 text-xs text-red-500">{errors.retirementFundTarget}</p>
              )}
            </div>
            <div>
              <label htmlFor="retirementFundCurrent" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Уже накоплено (₽)
              </label>
              <div className="relative">
                <input
                  type="text"
                  id="retirementFundCurrent"
                  value={formData.savings.retirementFund.current}
                  onChange={(e) => handleRetirementFundChange('current', e.target.value)}
                  className="form-input w-full pr-6"
                />
                <span className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400">₽</span>
              </div>
            </div>
            <div>
              <label htmlFor="retirementFundMonthlyContribution" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Ежемесячный взнос (₽)
              </label>
              <div className="relative">
                <input
                  type="text"
                  id="retirementFundMonthlyContribution"
                  value={formData.savings.retirementFund.monthlyContribution}
                  onChange={(e) => handleRetirementFundChange('monthlyContribution', e.target.value)}
                  className={`form-input w-full pr-6 ${errors.retirementFundContribution ? 'input-error' : ''}`}
                />
                <span className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400">₽</span>
              </div>
              {errors.retirementFundContribution && (
                <p className="mt-1 text-xs text-red-500">{errors.retirementFundContribution}</p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Финансовые цели */}
      <div className="mb-4">
        <div className="flex justify-between items-center mb-3">
          <h4 className="text-base font-medium text-gray-800 dark:text-gray-200 flex items-center">
            <Target className="w-4 h-4 mr-2 text-emerald-500" />
            Финансовые цели
          </h4>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {formData.savings.goals.length} {getGoalCountSuffix(formData.savings.goals.length)}
          </span>
        </div>

        {/* Список финансовых целей */}
        {formData.savings.goals.length === 0 ? (
          <div className="text-center p-6 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-dashed border-gray-300 dark:border-gray-700">
            <p className="text-gray-500 dark:text-gray-400">У вас пока нет финансовых целей</p>
          </div>
        ) : (
          <div className="space-y-4">
            {formData.savings.goals.map((goal, index) => (
              <div 
                key={goal.id}
                className="p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-100 dark:border-emerald-800"
              >
                <div className="flex justify-between items-start">
                  <div className="flex-grow">
                    {/* Название цели */}
                    <div className="mb-3">
                      <label htmlFor={`goal-name-${goal.id}`} className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Название цели
                      </label>
                      <input
                        type="text"
                        id={`goal-name-${goal.id}`}
                        value={goal.name}
                        onChange={(e) => updateGoal(goal.id, 'name', e.target.value)}
                        className={`form-input w-full ${errors[`goal_${index}_name`] ? 'input-error' : ''}`}
                      />
                      {errors[`goal_${index}_name`] && (
                        <p className="mt-1 text-xs text-red-500">{errors[`goal_${index}_name`]}</p>
                      )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {/* Целевая сумма */}
                      <div>
                        <label htmlFor={`goal-target-${goal.id}`} className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Требуемая сумма (₽)
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            id={`goal-target-${goal.id}`}
                            value={goal.target}
                            onChange={(e) => updateGoal(goal.id, 'target', e.target.value)}
                            className={`form-input w-full pr-6 ${errors[`goal_${index}_target`] ? 'input-error' : ''}`}
                          />
                          <span className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400">₽</span>
                        </div>
                        {errors[`goal_${index}_target`] && (
                          <p className="mt-1 text-xs text-red-500">{errors[`goal_${index}_target`]}</p>
                        )}
                      </div>

                      {/* Текущая сумма */}
                      <div>
                        <label htmlFor={`goal-current-${goal.id}`} className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Уже накоплено (₽)
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            id={`goal-current-${goal.id}`}
                            value={goal.current}
                            onChange={(e) => updateGoal(goal.id, 'current', e.target.value)}
                            className={`form-input w-full pr-6 ${errors[`goal_${index}_current`] ? 'input-error' : ''}`}
                          />
                          <span className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400">₽</span>
                        </div>
                        {errors[`goal_${index}_current`] && (
                          <p className="mt-1 text-xs text-red-500">{errors[`goal_${index}_current`]}</p>
                        )}
                      </div>

                      {/* Дедлайн */}
                      <div>
                        <label htmlFor={`goal-deadline-${goal.id}`} className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
                          <Clock className="w-3 h-3 mr-1" />
                          Срок достижения
                        </label>
                        <input
                          type="date"
                          id={`goal-deadline-${goal.id}`}
                          value={goal.deadline}
                          onChange={(e) => updateGoal(goal.id, 'deadline', e.target.value)}
                          className={`form-input w-full ${errors[`goal_${index}_deadline`] ? 'input-error' : ''}`}
                        />
                        {errors[`goal_${index}_deadline`] && (
                          <p className="mt-1 text-xs text-red-500">{errors[`goal_${index}_deadline`]}</p>
                        )}
                      </div>

                      {/* Приоритет */}
                      <div>
                        <label htmlFor={`goal-priority-${goal.id}`} className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Приоритет
                        </label>
                        <select
                          id={`goal-priority-${goal.id}`}
                          value={goal.priority}
                          onChange={(e) => updateGoal(goal.id, 'priority', e.target.value)}
                          className="form-select w-full"
                        >
                          <option value="high">Высокий</option>
                          <option value="medium">Средний</option>
                          <option value="low">Низкий</option>
                        </select>
                        
                        {/* Приоритет бейдж */}
                        <div className="mt-2">
                          <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded ${getPriorityClass(goal.priority)}`}>
                            {goal.priority === 'high' ? 'Высокий приоритет' : 
                             goal.priority === 'medium' ? 'Средний приоритет' : 'Низкий приоритет'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Кнопка удаления */}
                  <button
                    type="button"
                    onClick={() => removeGoal(goal.id)}
                    className="text-gray-400 hover:text-red-500 dark:hover:text-red-400 p-1 rounded-full transition-colors"
                    aria-label="Удалить цель"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Кнопка добавления цели */}
        <div className="mt-4">
          <Button
            type="button"
            onClick={addGoal}
            icon={<Plus className="w-4 h-4" />}
            variant="outline"
            size="sm"
          >
            Добавить финансовую цель
          </Button>
        </div>
      </div>

      {/* Рекомендации по сбережениям */}
      <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-100 dark:border-yellow-800">
        <div className="flex items-start">
          <AlertTriangle className="w-5 h-5 text-yellow-500 dark:text-yellow-400 flex-shrink-0 mt-0.5 mr-3" />
          <div>
            <h4 className="text-sm font-medium text-yellow-700 dark:text-yellow-300 mb-1">
              Важные советы по сбережениям
            </h4>
            <ul className="text-xs text-yellow-600 dark:text-yellow-400 space-y-1 list-disc list-inside">
              <li>Рекомендуется откладывать минимум 10% от дохода ежемесячно</li>
              <li>Начните с создания чрезвычайного фонда в размере 3-6 месячных расходов</li>
              <li>Распределяйте цели по приоритетам и начинайте с самых важных</li>
              <li>Для долгосрочных целей (более 5-10 лет) рассмотрите возможность инвестирования</li>
              <li>Автоматизируйте сбережения, настроив автоматические переводы в день зарплаты</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

// Вспомогательная функция для склонения слова "цель" в зависимости от числа
const getGoalCountSuffix = (count) => {
  const lastDigit = count % 10;
  const lastTwoDigits = count % 100;
  
  if (lastTwoDigits >= 11 && lastTwoDigits <= 19) {
    return 'целей';
  }
  
  if (lastDigit === 1) {
    return 'цель';
  }
  
  if (lastDigit >= 2 && lastDigit <= 4) {
    return 'цели';
  }
  
  return 'целей';
};

export default SavingsSection;